import {HttpParams} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {BreadcrumbDto, Catalog, CompoApiService, Environment, flatTreeTransform, LazyCatalog} from 'compob2b-libs';
import {Observable, Subject} from 'rxjs';
import {map, share} from 'rxjs/operators';
import {translations} from '../../../../environments/translations.ru';

@Injectable({
  providedIn: 'root'
})
export class CatalogService {
  apiUrl = undefined;

  readonly reloadCatalogSubject = new Subject<boolean>();

  enviropment = inject(Environment);

  constructor(protected readonly compoApiService: CompoApiService) {
    if (!this.apiUrl) {
      this.apiUrl = this.enviropment.accessRolesCatalog ? '/catalog/roles' : '/catalog';
    }
  }

  reloadCatalog(): void {
    this.reloadCatalogSubject.next(true);
  }

  isReloading(): Observable<boolean> {
    return this.reloadCatalogSubject.asObservable();
  }

  getCatalogTree(): Observable<Catalog[]> {
    const tree: Catalog[] = [{id: 1, header: translations.root, disabled: true}];

    return this.compoApiService.get<Catalog[]>(this.apiUrl)
      .pipe(
        map(response => flatTreeTransform<Catalog>(response.data, tree, translations.root, 'children'))
      );
  }

  getLazyCatalogTree(): Observable<LazyCatalog[]> {
    return this.compoApiService.get<LazyCatalog[]>(`${this.apiUrl}/lazy`)
      .pipe(
        share(),
        map(response => response.data),
      );
  }

  initCatalogTree(catalogId: number, showDeletedDisabled: boolean = false): Observable<LazyCatalog[]> {
    const params = new HttpParams().set('showDeletedDisabled', showDeletedDisabled);

    return this.compoApiService
      .getWithParams<LazyCatalog[]>(catalogId ? `${this.apiUrl}/root-lazy/${catalogId}` : `${this.apiUrl}/root`, params)
      .pipe(map(response => response.data));
  }

  getLazyChildren(id: number, showDeletedDisabled: boolean = false): Observable<LazyCatalog[]> {
    if (showDeletedDisabled) {
      return this.getAllLazyChildren(id, true);
    }

    return this.compoApiService.get<LazyCatalog[]>(`${this.apiUrl}/children/${id}`)
      .pipe(
        map(response => response.data)
      );
  }

  getAllLazyChildren(id: number, showDeleted: boolean): Observable<LazyCatalog[]> {
    const params = new HttpParams().set('showDeletedDisabled', showDeleted);
    return this.compoApiService.getWithParams<LazyCatalog[]>(`${this.apiUrl}/children/` + id, params)
      .pipe(
        map(response => response.data)
      );
  }

  getCatalog(): Observable<Catalog[]> {
    return this.compoApiService.get<Catalog[]>(this.apiUrl)
      .pipe(map(response => response.data));
  }

  searchCatalogsByHeader(term: string): Observable<Catalog[]> {
    const url = `${this.apiUrl}/search`;

    const params = new HttpParams().set('header', term);

    return this.compoApiService.getWithParams<Catalog[]>(url, params).pipe(
      map(response => response.data)
    );
  }

  getCatalogBreadCrumbs(id: number): Observable<BreadcrumbDto[]> {
    const uri = `/product/catalog-parent-chain/${id}`;
    return this.compoApiService.get<BreadcrumbDto[]>(uri)
      .pipe(
        map(response => {
          return [
            {id: null, header: translations.catalog},
            ...response.data
          ];
        }),
      );
  }

  isLastCatalog(id: number): Observable<boolean> {
    return this.compoApiService.get<boolean>(`${this.apiUrl}/last/` + id)
      .pipe(
        map(response => response.data)
      );
  }

  addChildrenToParentByParentId(id, node, childNodes) {
    let isSuccess = false;

    const {children} = node;
    if (node.id == id) {
      childNodes = [].concat(childNodes);
      node.children =
        ((Array.isArray(children) && children) || []).filter(child =>
          childNodes.every(node => node.id !== child.id)
        ).concat(childNodes);
      isSuccess = true;
    }
    else if (Array.isArray(children)) {
      isSuccess = children.some(child =>
        this.addChildrenToParentByParentId(id, child, childNodes)
      );
    }
    return isSuccess;
  }

  getParent(root, id) {
    let nodes;
    root.some((n) => {
      if (n.id == id) {
        return nodes = n;
      }
      if (n.children) {
        return nodes = this.getParent(n.children, id);
      }
    });
    return nodes || null;
  }

  private buildParentChildMap(tree: any[]): Map<number, any[]> {
    const parentChildMap = new Map<number, any[]>();

    tree.forEach(node => {
      const parentId = node.parentId;
      if (!parentChildMap.has(parentId)) {
        parentChildMap.set(parentId, []);
      }
      parentChildMap.get(parentId).push(node);
    });

    return parentChildMap;
  }

  treeRecursion(tree: any[], roots: any[]): void {
    const startTime = performance.now();

    const parentChildMap = this.buildParentChildMap(tree);

    const processNode = (node: any) => {
      const children = parentChildMap.get(node.id);
      if (children && children.length) {
        node.expanded = true;
        node.children = node.children || [];

        const newChildren = children.filter(child =>
          !node.children.some(existing => existing.id === child.id)
        );

        node.children.push(...newChildren);

        children.forEach(processNode);
      }
    };

    roots.forEach(processNode);

    const endTime = performance.now();
    console.log(`Время выполнения treeRecursion: ${endTime - startTime} мс`);

  }

}
